import { useCallback, type Ref } from "react";

import { Button } from "./Button";
import styles from "./CaseMenu.module.css";
import { MoreHorizontalIcon } from "./icons";
import { Menu, type MenuItem, type TriggerProps } from "./Menu";

function assign<T>(ref: Ref<T> | undefined, element: T | null) {
  if (typeof ref === "function") ref(element);
  else if (ref !== null && ref !== undefined) (ref as { current: T | null }).current = element;
}

/**
 * The trigger with both refs on it — the menu's own (its anchor) and the
 * caller's. One callback for as long as the two stay the same: a new one on
 * every render would hand the menu its anchor away and back each time.
 */
function MoreTrigger({
  menuRef,
  triggerRef,
  className,
  props,
}: {
  menuRef: TriggerProps["ref"];
  triggerRef: Ref<HTMLButtonElement> | undefined;
  className: string | undefined;
  props: Omit<TriggerProps, "ref">;
}) {
  const ref = useCallback(
    (element: HTMLButtonElement | null) => {
      assign(menuRef, element);
      assign(triggerRef, element);
    },
    [menuRef, triggerRef],
  );
  // The word is the name on every width; on a phone it is read, not shown,
  // and the key shows „⋯" (CaseMenu.module.css).
  return (
    <Button {...props} ref={ref} className={[styles.more, className].filter(Boolean).join(" ")}>
      <span className={styles.word}>Mehr …</span>
      <MoreHorizontalIcon size={18} className={styles.glyph} />
    </Button>
  );
}

/**
 * „Mehr …“ — the Vorgang's own gestures in its head (Task 9, UX-Gutachten 1,
 * B-1): „Form ändern …“, „Übergeben …“ and, apart and last, „Vorgang
 * stornieren …“ or „Wiederherstellen …“. Three buttons in a row of their own
 * made the record's head a line taller, and the other tabs had none; one
 * menu stands in the same place on every tab, beside the one red way on.
 *
 * On a phone a 40-px key „⋯" at the end of the title's line (UX-Gutachten 2,
 * B-12): as the widest button of the head it read as the screen's first
 * action. Its name stays „Mehr …".
 *
 * The behaviour is `Menu`'s — keys, focus back on the trigger before an item
 * acts, so a dialog it opens hands the focus back here. The trigger is an
 * ordinary outlined button with its word; `triggerRef` reaches it, for a
 * caller that puts the focus there itself (after a withdrawal, before a
 * dialog asked for in the address).
 */
export function CaseMenu({
  items,
  triggerRef,
  className,
}: {
  items: readonly MenuItem[];
  triggerRef?: Ref<HTMLButtonElement>;
  className?: string;
}) {
  return (
    <Menu
      label="Mehr …"
      items={items}
      trigger={({ ref, ...props }) => (
        <MoreTrigger menuRef={ref} triggerRef={triggerRef} className={className} props={props} />
      )}
    />
  );
}
